import React, { useState } from "react";
import { getVoteStats } from "./vote-utils";
import { useParams } from "react-router-dom";
import { useStore } from "../../helpers";
import { observer } from "mobx-react-lite";

export const OverallSummaryCard = observer(({ data }: any) => {
    const { total, voted, notVoted, percent } = getVoteStats(data);
    const { electionDayDashboardStore } = useStore();
    const { projectid } = useParams();
    const handleUpload = async (file: File) => {
        const id = projectid || data?.projectid;
        if (!id) {
            console.error("Project ID missing");
            return;
        }
        try {
            await electionDayDashboardStore.uploadVoterData(parseInt(id as string), file);
        } catch (error) {
            console.error("Upload failed:", error);
        }
    };
    const handleDownload = async () => {
        const id = projectid || data?.projectid;

        if (!id) {
            console.error("Project ID missing");
            return;
        }

        try {
            await electionDayDashboardStore.getElectionDayDataDownload(id);

            const blob = electionDayDashboardStore.downloadVoterData;

            if (!blob) {
                console.error("No API response");
                return;
            }

            // @ts-ignore
            const text = await blob.text();
            const json = JSON.parse(text);

            const filePath = json?.path;

            if (!filePath) {
                console.error("File path missing in response", json);
                return;
            }

            console.log("Correct filePath:", filePath);

            const fileUrl = `https://urn-assets.s3.ap-south-1.amazonaws.com/urn-assets${filePath}`;

            console.log("Downloading from:", fileUrl);

            window.open(fileUrl, "_blank");
        } catch (error) {
            console.error("Download failed:", error);
        }
    };

    const party = data?.gotv_party || {};

    const COLORS = [
        "bg-blue-500",
        "bg-orange-500",
        "bg-green-500",
        "bg-purple-500",
        "bg-pink-500",
        "bg-yellow-500",
        "bg-indigo-500",
    ];

    const sortedParties = Object.entries(party).sort((a: any, b: any) => b[1] - a[1]);

    const maxVotes = sortedParties[0]?.[1] || 1;

    return (
        <div className="rounded-2xl border bg-white p-6 shadow-md">
            <div className="mb-6 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-800">Overall Summary</h2>
                <p className="text-xs text-gray-400">Live election performance overview</p>
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={handleDownload}
                        disabled={electionDayDashboardStore.isLoading}
                        className={`rounded-lg px-4 py-2 text-xs font-semibold text-white shadow transition ${
                            electionDayDashboardStore.isLoading
                                ? "cursor-not-allowed bg-gray-300"
                                : "bg-blue-600 hover:bg-blue-700"
                        }`}
                    >
                        {electionDayDashboardStore.isLoading ? "Generating..." : "Download"}
                    </button>
                    <div className="flex gap-2">
                        {/* Hidden File Input */}
                        <input
                            type="file"
                            accept=".csv,.xlsx"
                            id="csvUpload"
                            className="hidden"
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleUpload(file);
                            }}
                            disabled={electionDayDashboardStore.isLoading}
                        />

                        {/* Upload Button */}
                        <label
                            htmlFor="csvUpload"
                            className={`cursor-pointer rounded-lg px-4 py-2 text-xs font-semibold text-white shadow transition ${
                                electionDayDashboardStore.isLoading
                                    ? "cursor-not-allowed bg-gray-300"
                                    : "bg-green-600 hover:bg-green-700"
                            }`}
                        >
                            Upload
                        </label>
                    </div>
                </div>
            </div>

            <div className="mb-6 grid grid-cols-3 gap-4">
                <div className="rounded-xl bg-gray-50 p-4 text-center">
                    <div className="text-xs text-gray-400">Total</div>
                    <div className="text-2xl font-bold text-gray-900">{total.toLocaleString()}</div>
                </div>

                <div className="rounded-xl bg-green-50 p-4 text-center">
                    <div className="text-xs text-green-600">Polled</div>
                    <div className="text-2xl font-bold text-green-700">{voted}</div>
                    {/*<div className="text-xs text-green-500">{votedPercent}%</div>*/}
                </div>

                <div className="rounded-xl bg-red-50 p-4 text-center">
                    <div className="text-xs text-red-600">Not Voted</div>
                    <div className="text-2xl font-bold text-red-600">{notVoted.toLocaleString()}</div>
                    {/*<div className="text-xs text-red-400">{notVotedPercent}%</div>*/}
                </div>
            </div>

            <div>
                <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Party Performance</span>

                    {/*{sortedParties.length > 0 && (*/}
                    {/*    <span className="text-xs text-gray-400">*/}
                    {/*        Leading:{" "}*/}
                    {/*        <span className="font-semibold text-gray-700">{sortedParties[0][0]}</span>*/}
                    {/*    </span>*/}
                    {/*)}*/}
                    {sortedParties.length > 0 &&
                        (() => {
                            const topValue = sortedParties[0][1];

                            const leaders = sortedParties
                                .filter(([_, value]) => value === topValue)
                                .map(([party]) => party);

                            return (
                                <span className="text-xs text-gray-400">
                                    Leading:{" "}
                                    <span className="font-semibold text-gray-700">{leaders.join(", ")}</span>
                                </span>
                            );
                        })()}
                </div>

                <div className="space-y-3">
                    {sortedParties.length === 0 && (
                        <div className="text-sm text-gray-400">No party data available</div>
                    )}

                    {sortedParties.map(([key, val]: any, index: number) => {
                        const color = COLORS[index % COLORS.length];
                        const percent = voted > 0 ? ((val / voted) * 100).toFixed(1) : "0";

                        return (
                            <div key={key}>
                                <div className="mb-1 flex justify-between text-sm">
                                    <span className="flex items-center gap-2">
                                        <span className={`h-2.5 w-2.5 rounded-full ${color}`} />
                                        {key}
                                    </span>

                                    <span className="font-semibold">
                                        {val} ({percent}%)
                                    </span>
                                </div>

                                <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                                    <div
                                        className={`${color} h-2 transition-all duration-500`}
                                        style={{
                                            width: `${percent}%`,
                                        }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
});
